import React, {useEffect, useState} from 'react';
import CertificateService from "../../../../../services/CertificateService";
import {Button, Flex, Form, Input, message, Modal, Select, Table} from "antd";
import IntlMessage from "../../../../../components/IntlMessage/IntlMessage";
import classes from "../certificate.module.css";
import GreyButton from "../../../../../components/UI/Buttons/GreyButton";
import {clrs} from "../../../../../constants/colors";
import FormItem from "antd/es/form/FormItem";
import {MinusCircleOutlined, PlusOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import MyText from "../../../../../components/UI/Text/MyText";
import {Document, Page} from "react-pdf";
import {API_BASE_URL} from "../../../../../constants/api";
import {useDispatch, useSelector} from "react-redux";
import {getAllCertificateTemplates} from "../../../../../store/slices/CertificateSlice";
import debounce from "lodash/debounce";

const CertificateTemplateList = () => {
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(30);

    const {certificateTemplates, isLoading, hasMore, error} = useSelector(state => state.certificateList)

    const [initialLoading, setInitialLoading] = useState(true);

    const [editingTemplate, setEditingTemplate] = useState(null);


    const [openCreateModal, setOpenCreateModal] = useState(false)
    const [form] = Form.useForm();
    const name = Form.useWatch('name', form);
    const template = Form.useWatch('template', form);

    const [viewCertificateTemplate, setViewCertificateTemplate] = useState(false)
    const [openViewCertificateTemplate, setOpenViewCertificateTemplate] = useState(false)
    const [certificateTemplateNumPages, setCertificateTemplateNumPages] = useState();
    const [certificateTemplatePageNumber, setCertificateTemplatePageNumber] = useState(1);

    useEffect(() => {
        const debouncedFetch = debounce(() => {
            dispatch(getAllCertificateTemplates({
                page: currentPage,
                limit: pageSize
            }));
        }, 500);

        debouncedFetch();

        return () => {
            debouncedFetch.cancel();
        };
    }, [currentPage, pageSize]);

    useEffect(() => {
        setCurrentPage(1);
    }, [pageSize]);

    async function deleteCertificateTemplate(id) {
        await CertificateService.deleteCertificateTemplate(id)
            .then((r) => {
                message.success(<IntlMessage id={'certificate.templateIsDeleted'}/>, 5);
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            })
            .finally(() => {
                dispatch(getAllCertificateTemplates({page: currentPage, limit: pageSize}))
            })
    }

    const columns = [
        {
            title: <IntlMessage id={'name2'}/>,
            render: (_, record) => (
                <p>{record.name}</p>
            ),
            width: '25%',
        },
        {
            title: <IntlMessage id={'certificate.templateName'}/>,
            render: (_, record) => (
                <p>{record.template}</p>
            ),
            width: '25%',
        },
        {
            title: <IntlMessage id={'certificate.codes'}/>,
            render: (_, record) => (
                <>
                    {record?.codeNames.length > 0 && record.codeNames.map((code, index) => (
                        <p key={index}>
                            {code.code + " - " + code.name}
                        </p>
                    ))}
                </>
            ),
            width: '25%',
        },
        {
            title: <IntlMessage id={'actions'}/>,
            render: (_, record) => (
                <>
                    {
                        <Flex style={{width: "100%"}} align={"center"} justify={"center"} wrap={"wrap"} gap={20}>
                            <p onClick={() => {
                                setViewCertificateTemplate(record);
                                setOpenViewCertificateTemplate(true);
                            }} className={classes.copyLinkBtn}><IntlMessage id={'view'}/></p>
                            <p
                                onClick={() => {
                                    setEditingTemplate(record); // record — текущий шаблон
                                    // Преобразовать codeNames в [{code, name}]
                                    const pairs = record.codeNames?.map(({code, name}) => ({code, name})) || [];
                                    form.setFieldsValue({
                                        name: record.name,
                                        template: record.template,
                                        codeNamePairs: pairs,
                                    });
                                    setOpenCreateModal(true);
                                }}
                                className={classes.copyLinkBtn}
                            >
                                <IntlMessage id={'edit'}/>
                            </p>
                            <p onClick={() => downloadExcelTemplate(record.id)} className={classes.excelBtn}><IntlMessage id={'certificate.excelFormat'}/></p>
                        </Flex>
                    }
                </>
            ),
            width: '25%',
        },
    ];

    const handlePaginationChange = (page, pageSize) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    }

    function onDocumentLoadSuccess({numPages}: { numPages: number }): void {
        setCertificateTemplateNumPages(numPages);
    }

    const transformToMap = (fields) => {
        const map = {};
        fields.forEach(({code, name}) => {
            if (code && name) map[code] = name;
        });
        return map;
    };

    const handleCreateTemplate = async () => {
        const values = form.getFieldsValue();
        const hashMap = transformToMap(values.codeNamePairs || []);

        try {
            await form.validateFields();
            message.loading(<IntlMessage id="loading" />, 0);

            const action = editingTemplate
                ? CertificateService.editCertificateTemplate(editingTemplate.id, name, template, hashMap)
                : CertificateService.saveCertificateTemplate(name, template, hashMap);

            await action;

            message.destroy();
            message.success(<IntlMessage id="success" />, 5);
            form.resetFields();
        } catch (error) {
            message.destroy();
            message.error(<IntlMessage id="error" />, 5);
        } finally {
            dispatch(getAllCertificateTemplates({ page: currentPage, limit: pageSize }));
            setOpenCreateModal(false);
        }
    };

    async function downloadExcelTemplate(templateId) {
        message.loading(<IntlMessage id={'loading'}/>);
        await CertificateService.getExcelByTemplateId(templateId)
            .then((r) => {
                message.destroy();
                message.success(<IntlMessage id={'success'}/>, 3);
                triggerDownload(r.data, "template.xlsx")
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 3);
            })
    }

    function triggerDownload(blob, filename) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    return (
        <Flex style={{width: "100%"}} align={"flex-start"} justify={"flex-start"} gap={20} vertical>
            <Flex style={{width: '100%'}} gap={10}>
                <GreyButton onClick={() => {
                    setOpenCreateModal(true);
                }} backgroundColor={clrs.whiter} fontColor={clrs.black}><IntlMessage id={'certificate.createTemplate'}/>
                </GreyButton>
            </Flex>

            {/*<BlockLoading isLoading={formSave.isLoading || application.isLoading || loadingFormMultipleSave}/>         */}
            <Table
                bordered
                columns={columns}
                dataSource={certificateTemplates}
                pagination={{
                    current: currentPage,
                    total: hasMore
                        ? currentPage * pageSize + 1
                        : currentPage * pageSize,
                    defaultPageSize: pageSize,
                    showSizeChanger: true,
                    pageSize: pageSize,
                    pageSizeOptions: ['5', '10', '20', '30', '50', '100'],
                    onChange: handlePaginationChange,
                    // onShowSizeChange: handlePaginationChange,
                }}
                rowKey={record => record.id}
                loading={isLoading}
                style={{
                    width: "100%",
                }}
                scroll={{x: 800}}
            />
            <Form form={form} className={classes.form}>
                <Modal
                    width={400}
                    maskClosable={true}
                    title={<IntlMessage id={'certificate.creationTemplate'}/>}
                    open={openCreateModal}
                    okText={<IntlMessage id={'save'}/>}
                    cancelText={<IntlMessage id={'close'}/>}
                    onOk={() => {
                        setOpenCreateModal(false)
                    }}
                    onCancel={() => {
                        form.resetFields();
                        setOpenCreateModal(false)
                    }}
                    footer={[
                        <Button key="submit" type="primary" onClick={() => handleCreateTemplate()}>
                            <IntlMessage id={editingTemplate ? "edit" : "create"}/>
                        </Button>
                    ]}
                >
                    {
                        openCreateModal &&
                        <Flex style={{width: "100%", position: "relative"}} gap={10} vertical>
                            <FormItem rules={[{required: true, message: <IntlMessage id={'requiredField'}/>}]}
                                      className={classes.formInputBlock} name={"name"}
                                      labelCol={{span: 24}}
                            >
                                <Input placeholder={t('certificate.templateName')} rootClassName={classes.formInput}/>
                            </FormItem>
                            <FormItem rules={[{required: true, message: <IntlMessage id={'requiredField'}/>}]}
                                      className={classes.formInputBlock} name={"template"}
                            >
                                <Select
                                    options={[
                                        {
                                            value: 'foundation_template.html',
                                            label: 'Foundation',
                                        },
                                        {
                                            value: 'uslovniki_template.html',
                                            label: 'Uslovniki',
                                        },
                                        {
                                            value: 'generalEnglish_template.html',
                                            label: 'General English',
                                        },
                                        {
                                            value: 'mathematics_template.html',
                                            label: 'Mathematics',
                                        },
                                        {
                                            value: 'robotics_template.html',
                                            label: 'Robotics',
                                        },
                                        {
                                            value: 'microqualification_template.html',
                                            label: 'Microqualifications',
                                        },
                                        {
                                            value: 'others.html',
                                            label: 'Others',
                                        },
                                        {
                                            value: 'inclusive_template.html',
                                            label: 'Inclusive',
                                        },
                                        {
                                            value: 'sunday_open_template.html',
                                            label: 'Sunday Open',
                                        },
                                    ]}
                                    placeholder={<IntlMessage id={'certificate.htmlFile'}/>}
                                />
                            </FormItem>
                            {/* Динамические пары code:name */}
                            <Form.List name="codeNamePairs" rules={[{required: true, message: t('requiredField')}]}>
                                {(fields, {add, remove}) => (
                                    <Flex vertical gap={10}>
                                        {fields.map(({key, name, ...restField}) => (
                                            <Flex key={key} gap={"small"} justify={"flex-start"} align={"baseline"}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'code']}
                                                    className={classes.formInputBlock}
                                                    rules={[{required: true, message: t('requiredField')}]}
                                                >
                                                    <Input placeholder={t('Сode')}/>
                                                </Form.Item>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'name']}
                                                    className={classes.formInputBlock}
                                                    rules={[{required: true, message: t('requiredField')}]}
                                                >
                                                    <Input placeholder={t('name2')}/>
                                                </Form.Item>
                                                <MinusCircleOutlined onClick={() => remove(name)}/>
                                            </Flex>
                                        ))}
                                        <Form.Item>
                                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined/>}>
                                                <IntlMessage id={"certificate.addField"}/>
                                            </Button>
                                        </Form.Item>
                                    </Flex>
                                )}
                            </Form.List>
                        </Flex>
                    }
                </Modal>
            </Form>
            <Modal
                width={800}
                maskClosable={true}
                // title={<IntlMessage id={'contractsPage.details'}/>}
                open={openViewCertificateTemplate}
                okText={<IntlMessage id={'save'}/>}
                cancelText={<IntlMessage id={'close'}/>}
                style={{
                    top: 20,
                }}
                onOk={() => {
                    setOpenViewCertificateTemplate(false)
                }}
                onCancel={() => {
                    setOpenViewCertificateTemplate(false)
                }}
                footer={[
                    <Button key="submit" type="primary" onClick={() => {
                        setOpenViewCertificateTemplate(false)
                    }}>
                        OK
                    </Button>
                ]}
            >
                {
                    viewCertificateTemplate &&
                    <Flex gap={50}
                          style={{
                              width: "100%",
                              minHeight: "calc(100vh - 300px)",
                              position: "relative",
                              padding: "0px 20px 20px 0px",
                          }}
                          vertical
                          align={"flex-start"}
                          justify={"flex-start"}>
                        <Flex align={"center"} justify={"center"} vertical
                              style={{overflowX: "auto", overflowY: "hidden", width: "100%"}}>
                            <Flex style={{
                                zIndex: 1,
                                padding: "10px 20px",
                                backgroundColor: "white",
                                borderRadius: 10,
                                boxShadow: "0px 0px 10px 0px rgba(0,0,0,0.1)",
                            }} gap={15} justify={"space-between"} align={"center"}>
                                <Button disabled={certificateTemplatePageNumber === 1} onClick={() => {
                                    setCertificateTemplatePageNumber(certificateTemplatePageNumber - 1)
                                }}>
                                    {'<'}
                                </Button>
                                <MyText size={"small"}>
                                    {certificateTemplatePageNumber} / {certificateTemplateNumPages}
                                </MyText>
                                <Button disabled={certificateTemplatePageNumber === certificateTemplateNumPages}
                                        onClick={() => {
                                            setCertificateTemplatePageNumber(certificateTemplatePageNumber + 1)
                                        }}>
                                    {'>'}
                                </Button>
                            </Flex>
                            <Document
                                file={API_BASE_URL + "/api/certificate/resource/template/" + viewCertificateTemplate?.id}
                                onLoadSuccess={onDocumentLoadSuccess}
                                loading={<IntlMessage id={'documentIsLoading'}/>}
                            >
                                <Page pageNumber={certificateTemplatePageNumber} width={700}/>
                            </Document>
                        </Flex>
                    </Flex>
                }
            </Modal>
        </Flex>
    );
};

export default CertificateTemplateList;